import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Pencil, Star, Check, X, Lock } from "lucide-react";
import { AREAS, BADGE_LABELS } from "@/lib/projectMeta";

const API = process.env.REACT_APP_BACKEND_URL;
const STATUS_OPTS = ["idea", "in_progettazione", "progetto_pilota", "attivo"];
const BADGE_OPTS = Object.keys(BADGE_LABELS);
const emptyProject = {
  title: "", area: "territorio", payoff: "", summary: "", content: "",
  objectives: "", results: "", needs: "", partners_sought: "",
  image_url: "", is_concept: false, status: "in_progettazione", badges: [],
  fund_target: "", fund_raised: "", supporters_count: "", featured: false, order: 0, active: true,
};
const toLines = (s) => (s || "").split("\n").map((x) => x.trim()).filter(Boolean);
const fromLines = (arr) => (arr || []).join("\n");

export default function AdminProjectsPage() {
  const [pw, setPw] = useState(localStorage.getItem("pa_admin_pw") || "");
  const [authed, setAuthed] = useState(false);
  const [tab, setTab] = useState("progetti");
  const [projects, setProjects] = useState([]);
  const [comments, setComments] = useState([]);
  const [interests, setInterests] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const H = useCallback(() => ({ "Content-Type": "application/json", "X-Admin-Password": pw }), [pw]);

  const loadAll = useCallback(async () => {
    const [p, c, i] = await Promise.all([
      fetch(`${API}/api/admin/projects`, { headers: H() }),
      fetch(`${API}/api/admin/comments`, { headers: H() }),
      fetch(`${API}/api/admin/project-interests`, { headers: H() }),
    ]);
    if (!p.ok) { setAuthed(false); localStorage.removeItem("pa_admin_pw"); toast.error("Password errata."); return; }
    setProjects(await p.json()); setComments(await c.json()); setInterests(await i.json());
    setAuthed(true); localStorage.setItem("pa_admin_pw", pw);
  }, [H, pw]);

  useEffect(() => { if (pw) loadAll(); /* eslint-disable-next-line */ }, []);

  const login = (e) => { e.preventDefault(); loadAll(); };

  const openNew = () => setEditing({ ...emptyProject });
  const openEdit = (p) => setEditing({
    ...p, objectives: fromLines(p.objectives), results: fromLines(p.results),
    needs: fromLines(p.needs), partners_sought: fromLines(p.partners_sought),
    fund_target: p.fund_target ?? "", fund_raised: p.fund_raised ?? "", supporters_count: p.supporters_count ?? "",
  });

  const save = async () => {
    if (!editing.title.trim() || !editing.summary.trim() || !editing.content.trim()) {
      toast.error("Compila Titolo, Sommario e Descrizione."); return;
    }
    setBusy(true);
    const body = {
      ...editing,
      objectives: toLines(editing.objectives), results: toLines(editing.results),
      needs: toLines(editing.needs), partners_sought: toLines(editing.partners_sought),
      fund_target: editing.fund_target === "" ? null : Number(editing.fund_target),
      fund_raised: editing.fund_raised === "" ? null : Number(editing.fund_raised),
      supporters_count: editing.supporters_count === "" ? null : Number(editing.supporters_count),
      order: Number(editing.order) || 0,
    };
    const isNew = !editing.id;
    const url = isNew ? `${API}/api/admin/projects` : `${API}/api/admin/projects/${editing.id}`;
    const r = await fetch(url, { method: isNew ? "POST" : "PUT", headers: H(), body: JSON.stringify(body) });
    setBusy(false);
    if (!r.ok) { toast.error("Errore nel salvataggio."); return; }
    toast.success("Progetto salvato."); setEditing(null); loadAll();
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo progetto?")) return;
    await fetch(`${API}/api/admin/projects/${id}`, { method: "DELETE", headers: H() });
    toast.success("Eliminato."); loadAll();
  };

  const toggleFeatured = async (p) => {
    await fetch(`${API}/api/admin/projects/${p.id}`, { method: "PUT", headers: H(),
      body: JSON.stringify({ ...p, featured: !p.featured }) });
    loadAll();
  };

  const moderate = async (id, status) => {
    await fetch(`${API}/api/admin/comments/${id}?status=${status}`, { method: "PUT", headers: H() });
    loadAll();
  };
  const delComment = async (id) => {
    await fetch(`${API}/api/admin/comments/${id}`, { method: "DELETE", headers: H() });
    loadAll();
  };

  const input = "w-full rounded-lg border-2 border-[#E5E1D8] bg-white px-3 py-2 text-sm focus:border-[#2E4F3B] focus:outline-none";

  if (!authed) return (
    <div className="min-h-screen bg-[#1f2d22] flex items-center justify-center p-6">
      <form onSubmit={login} className="bg-white rounded-2xl p-8 w-full max-w-sm" data-testid="admin-proj-login">
        <Lock className="w-8 h-8 text-[#2E4F3B] mb-4" />
        <h1 className="font-display text-2xl font-black text-[#1A1A1A] mb-4">Gestione Progetti</h1>
        <input type="password" placeholder="Password admin" value={pw} onChange={(e) => setPw(e.target.value)} className={input} data-testid="admin-proj-pw" />
        <button className="mt-4 w-full rounded-full bg-[#2E4F3B] text-white font-bold py-3" data-testid="admin-proj-enter">Entra</button>
      </form>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#FBF9F6]">
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8">
        <h1 className="font-display text-3xl font-black text-[#1A1A1A] mb-6">Gestione Progetti</h1>
        <div className="flex gap-2 mb-6">
          {[["progetti", `Progetti (${projects.length})`], ["commenti", `Commenti (${comments.filter(c=>c.status==='pending').length} da moderare)`], ["interessi", `Interessi (${interests.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 rounded-full text-sm font-bold ${tab === k ? "bg-[#2E4F3B] text-white" : "bg-white border border-[#E5E1D8] text-[#1A1A1A]"}`}>{l}</button>
          ))}
        </div>

        {tab === "progetti" && (
          <>
            <button onClick={openNew} data-testid="admin-new-project" className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#C15C3D] text-white font-bold px-5 py-2.5 text-sm"><Plus className="w-4 h-4" />Nuovo progetto</button>
            <div className="space-y-2">
              {projects.map((p) => (
                <div key={p.id} className="bg-white rounded-xl border border-[#E5E1D8] p-4 flex items-center gap-4" data-testid={`admin-project-${p.slug}`}>
                  <img src={p.image_url} alt="" className="w-14 h-14 rounded-lg object-cover bg-[#FBF9F6]" />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-[#1A1A1A] truncate">{p.title}</div>
                    <div className="text-xs text-[#5C5C5C]">{p.area} · {p.status} · 👍{p.like_count} 👎{p.dislike_count}</div>
                  </div>
                  <button onClick={() => toggleFeatured(p)} title="In evidenza" data-testid={`admin-feature-${p.slug}`}><Star className={`w-5 h-5 ${p.featured ? "fill-[#E8C9A0] text-[#E8C9A0]" : "text-[#c9c9c9]"}`} /></button>
                  <button onClick={() => openEdit(p)} data-testid={`admin-edit-${p.slug}`}><Pencil className="w-4 h-4 text-[#2E4F3B]" /></button>
                  <button onClick={() => del(p.id)} data-testid={`admin-del-${p.slug}`}><Trash2 className="w-4 h-4 text-[#C15C3D]" /></button>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "commenti" && (
          <div className="space-y-2">
            {comments.length === 0 && <p className="text-[#5C5C5C] italic">Nessun commento.</p>}
            {comments.map((c) => (
              <div key={c.id} className="bg-white rounded-xl border border-[#E5E1D8] p-4" data-testid={`admin-comment-${c.id}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${c.status === "approved" ? "bg-green-100 text-green-700" : c.status === "rejected" ? "bg-red-100 text-red-700" : "bg-yellow-100 text-yellow-700"}`}>{c.status}</span>
                  <span className="text-xs text-[#5C5C5C]">{c.project_title}</span>
                </div>
                <p className="mt-2 text-sm text-[#1A1A1A]">"{c.text}"</p>
                <p className="text-xs text-[#5C5C5C] mt-1">— {c.name} {c.surname || ""} {c.email ? `· ${c.email}` : ""}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => moderate(c.id, "approved")} className="inline-flex items-center gap-1 text-xs font-bold text-green-700 border border-green-200 rounded-full px-3 py-1"><Check className="w-3 h-3" />Approva</button>
                  <button onClick={() => moderate(c.id, "rejected")} className="inline-flex items-center gap-1 text-xs font-bold text-red-700 border border-red-200 rounded-full px-3 py-1"><X className="w-3 h-3" />Rifiuta</button>
                  <button onClick={() => delComment(c.id)} className="inline-flex items-center gap-1 text-xs font-bold text-[#5C5C5C] border border-[#E5E1D8] rounded-full px-3 py-1"><Trash2 className="w-3 h-3" />Elimina</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "interessi" && (
          <div className="space-y-2">
            {interests.length === 0 && <p className="text-[#5C5C5C] italic">Nessuna richiesta ricevuta.</p>}
            {interests.map((it) => (
              <div key={it.id} className="bg-white rounded-xl border border-[#E5E1D8] p-4" data-testid={`admin-interest-${it.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-bold text-[#1A1A1A]">{it.first_name} {it.last_name || ""}</span>
                  <span className="text-xs text-[#C15C3D] font-bold">{it.reason}</span>
                </div>
                <p className="text-xs text-[#5C5C5C]">{it.project_title} · {it.contact_type}</p>
                <p className="text-sm mt-1">{it.email} {it.phone ? `· ${it.phone}` : ""}</p>
                {it.message && <p className="text-sm text-[#3F3F3F] mt-2">{it.message}</p>}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Editor modal */}
      {editing && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-start justify-center p-3 sm:p-6 overflow-y-auto" onClick={() => setEditing(null)}>
          <div className="bg-white rounded-2xl w-full max-w-2xl my-4 p-6" onClick={(e) => e.stopPropagation()} data-testid="admin-project-editor">
            <h2 className="font-display text-2xl font-black mb-4">{editing.id ? "Modifica progetto" : "Nuovo progetto"}</h2>
            <div className="space-y-3">
              <input className={input} placeholder="Titolo" value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} data-testid="editor-title" />
              <div className="grid sm:grid-cols-2 gap-3">
                <select className={input} value={editing.area} onChange={(e) => setEditing({ ...editing, area: e.target.value })}>
                  {AREAS.map((a) => <option key={a.slug} value={a.slug}>{a.name}</option>)}
                </select>
                <select className={input} value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })}>
                  {STATUS_OPTS.map((s) => <option key={s} value={s}>{BADGE_LABELS[s]}</option>)}
                </select>
              </div>
              <input className={input} placeholder="Payoff (frase breve)" value={editing.payoff} onChange={(e) => setEditing({ ...editing, payoff: e.target.value })} />
              <textarea className={input} rows={2} placeholder="Sommario" value={editing.summary} onChange={(e) => setEditing({ ...editing, summary: e.target.value })} />
              <textarea className={input} rows={4} placeholder="Descrizione" value={editing.content} onChange={(e) => setEditing({ ...editing, content: e.target.value })} />
              <input className={input} placeholder="URL immagine" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} />
              <div className="grid sm:grid-cols-2 gap-3">
                <textarea className={input} rows={3} placeholder="Obiettivi (uno per riga)" value={editing.objectives} onChange={(e) => setEditing({ ...editing, objectives: e.target.value })} />
                <textarea className={input} rows={3} placeholder="Risultati (uno per riga)" value={editing.results} onChange={(e) => setEditing({ ...editing, results: e.target.value })} />
                <textarea className={input} rows={3} placeholder="Cosa serve (uno per riga)" value={editing.needs} onChange={(e) => setEditing({ ...editing, needs: e.target.value })} />
                <textarea className={input} rows={3} placeholder="Partner cercati (uno per riga)" value={editing.partners_sought} onChange={(e) => setEditing({ ...editing, partners_sought: e.target.value })} />
              </div>
              <div>
                <div className="text-xs font-bold uppercase text-[#5C5C5C] mb-2">Badge</div>
                <div className="flex flex-wrap gap-2">
                  {BADGE_OPTS.map((b) => (
                    <label key={b} className={`text-xs font-bold px-3 py-1.5 rounded-full cursor-pointer border-2 ${editing.badges.includes(b) ? "bg-[#2E4F3B] text-white border-[#2E4F3B]" : "border-[#E5E1D8] text-[#1A1A1A]"}`}>
                      <input type="checkbox" className="hidden" checked={editing.badges.includes(b)}
                        onChange={(e) => setEditing({ ...editing, badges: e.target.checked ? [...editing.badges, b] : editing.badges.filter((x) => x !== b) })} />
                      {BADGE_LABELS[b]}
                    </label>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <input className={input} type="number" placeholder="Obiettivo €" value={editing.fund_target} onChange={(e) => setEditing({ ...editing, fund_target: e.target.value })} />
                <input className={input} type="number" placeholder="Raccolti €" value={editing.fund_raised} onChange={(e) => setEditing({ ...editing, fund_raised: e.target.value })} />
                <input className={input} type="number" placeholder="Sostenitori" value={editing.supporters_count} onChange={(e) => setEditing({ ...editing, supporters_count: e.target.value })} />
              </div>
              <div className="flex flex-wrap items-center gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />In evidenza</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.is_concept} onChange={(e) => setEditing({ ...editing, is_concept: e.target.checked })} />Concept</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={editing.active} onChange={(e) => setEditing({ ...editing, active: e.target.checked })} />Attivo</label>
                <label className="flex items-center gap-2">Ordine <input type="number" className="w-16 rounded border-2 border-[#E5E1D8] px-2 py-1" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} /></label>
              </div>
            </div>
            <div className="mt-5 flex gap-3">
              <button onClick={save} disabled={busy} data-testid="editor-save" className="inline-flex items-center gap-2 rounded-full bg-[#2E4F3B] text-white font-bold px-6 py-3">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}Salva</button>
              <button onClick={() => setEditing(null)} className="rounded-full border-2 border-[#E5E1D8] font-bold px-6 py-3">Annulla</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
